/**
 * Shared live-sync contract: both the api hooks (which read refetch signals) and
 * the nop-events WS provider (which writes them, via the `liveSync` slice) depend
 * on it downward — neither imports the other.
 */

/**
 * Command-center aggregates the FE refetches on demand. These strings are the
 * contract with the BE gateway's `changed[]` payload.
 */
export const AGGREGATE_KEYS = [
  'activeCases',
  'newCases',
  'surgeons',
  'hubsResourceReport',
  'simpleStatistics',
  'aircraftStatuses',
  // TMDXNC-13 — reserved for the weather snapshot query's `useAggregateRefetch`
  // key; the BE gateway's `changed[]` payload doesn't emit it yet (weather
  // refresh is Redis-snapshot + timer-polled, not live-sync-pushed), so this
  // signal never bumps today — the hook still gets its timer-based poll via
  // `usePollingQuery`'s `getDelay` fallback.
  'weatherSnapshot',
  'caseTimeline',
] as const;

export type AggregateKey = (typeof AGGREGATE_KEYS)[number];

/** Per-aggregate refetch counter; a bump tells that aggregate's query to refetch. */
export type SignalMap = Record<AggregateKey, number>;

export const isAggregateKey = (value: string): value is AggregateKey =>
  (AGGREGATE_KEYS as readonly string[]).includes(value);

export const initialSignals: SignalMap = AGGREGATE_KEYS.reduce((acc, key) => {
  acc[key] = 0;
  return acc;
}, {} as SignalMap);

/**
 * Increment counters for the known aggregates named in `changed`. Unknown keys are
 * ignored; returns the same reference when nothing known changed to avoid re-renders.
 */
export const bumpSignals = (
  current: SignalMap,
  changed: string[],
): SignalMap => {
  const known = changed.filter(isAggregateKey);
  if (known.length === 0) {
    return current;
  }
  const next = { ...current };
  for (const key of known) {
    next[key] += 1;
  }
  return next;
};

/** Bump every aggregate — used for the cold resync on (re)connect. */
export const bumpAll = (current: SignalMap): SignalMap => {
  const next = { ...current };
  for (const key of AGGREGATE_KEYS) {
    next[key] += 1;
  }
  return next;
};

/**
 * Resources whose data can be updated by a live-sync cache patch rather than a
 * refetch. Deliberately NOT `AggregateKey`: that vocabulary answers "what should
 * refetch" and this one answers "what was updated, when". They do not align —
 * `adminUsers` and `domainEvents` are patched but are not aggregates, and
 * `weatherSnapshot` is an aggregate that is never patched.
 *
 * Only these need a stamp. Everything else advances `fulfilledTimeStamp` on its
 * own, because it is refreshed by a fetch.
 */
export const LIVE_RESOURCES = [
  'activeCases',
  'newCases',
  'adminUsers',
  'domainEvents',
  'alerts',
] as const;

export type LiveResource = (typeof LIVE_RESOURCES)[number];

/**
 * Wall-clock instant of the last live patch per resource. Partial: an absent key
 * means "never patched", which is NOT the same as patched at epoch zero — a
 * frame's own `ts` defaults to 0 and synthesized catch-up frames set it to 0, so
 * a 0 here would read as 1970.
 */
export type LiveUpdateMap = Partial<Record<LiveResource, number>>;

/** Shape of the `liveSync` Redux slice — the shared refetch-signal + connection state. */
export interface LiveSyncState {
  signals: SignalMap;
  connected: boolean;
  /** The feed has connected at least once this page load; never reset. */
  hasConnected: boolean;
  lastLiveUpdateAt: LiveUpdateMap;
  /** The page load's domain-event history backfill has finished, failed or found nothing to do. */
  domainEventHistoryBackfillSettled: boolean;
}

/** Reducer-path / selector key for the slice. Both the slice and the api-side reader use it. */
export const LIVE_SYNC_SLICE_NAME = 'liveSync';

export const initialLiveSyncState: LiveSyncState = {
  signals: initialSignals,
  connected: false,
  hasConnected: false,
  lastLiveUpdateAt: {},
  domainEventHistoryBackfillSettled: false,
};

/**
 * Minimal store shape for reading the slice. Narrow (not `RootState`) so the api
 * reader selects without importing the store — that would form an api → store cycle.
 */
export interface WithLiveSync {
  [LIVE_SYNC_SLICE_NAME]: LiveSyncState;
}

export const selectLiveSyncConnected = (state: WithLiveSync): boolean =>
  state.liveSync.connected;

/**
 * True only once a connected feed has dropped. Kill-switch mode never dispatches
 * `connectionChanged(true)`, so it stays false there.
 */
export const selectLiveFeedDisconnected = (state: WithLiveSync): boolean =>
  state.liveSync.hasConnected && !state.liveSync.connected;

export const selectAggregateSignal =
  (key: AggregateKey) =>
  (state: WithLiveSync): number =>
    state.liveSync.signals[key];

export const selectLiveUpdateMap = (state: WithLiveSync): LiveUpdateMap =>
  state.liveSync.lastLiveUpdateAt;

export const selectLiveUpdateAt =
  (key: LiveResource) =>
  (state: WithLiveSync): number | null =>
    state.liveSync.lastLiveUpdateAt[key] ?? null;

export const selectDomainEventHistoryBackfillSettled = (
  state: WithLiveSync,
): boolean => state.liveSync.domainEventHistoryBackfillSettled;
